import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Container, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import MyContext from '@/shared/contexts/AppContext';
import RelatoriosCorregedoriaList from '@/modules/reports/presentation/components/RelatoriosCorregedoriaList';
import RelatoriosCorregedoriaFilter, { CorregedoriaFilterOptions } from '@/modules/reports/presentation/components/RelatoriosCorregedoriaFilter';
import ModalReprovacao from '@/modules/reports/presentation/components/ModalReprovacao';
import ModalValidarRemotos from '@/modules/reports/presentation/components/ModalValidarRemotos';
import {
  BuscarRelatoriosParaCorregedoria,
  AprovarCorregedoria,
  ReprovarCorregedoria,
  RemotoValidadoItem,
} from '@/modules/reports/application/relatorios-corregedoria.service';
import { RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.service';
import Links from '@/shared/constants/navegacao';
import RelatoriosUserLabel from '@/modules/reports/presentation/components/RelatoriosUserLabel';
import { useNavigationView } from '@/shared/hooks/useNavigationView';

export default function RelatoriosCorregedoriaPage() {
  const context = useContext(MyContext);
  const navigate = useNavigate();

  if (!context) {
    throw new Error('RelatoriosCorregedoriaPage deve ser usado dentro de um MyProvider');
  }

  const [loading, setLoading] = useState(true);
  const [relatorios, setRelatorios] = useState<RelatorioAtividadeDto[]>([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [relatorioParaReprovar, setRelatorioParaReprovar] = useState<RelatorioAtividadeDto | null>(null);
  const [showValidarRemotosModal, setShowValidarRemotosModal] = useState(false);
  const [relatorioParaAprovar, setRelatorioParaAprovar] = useState<RelatorioAtividadeDto | null>(null);
  const [aproving, setAproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const { permissions } = useNavigationView();

  /** Preserva o último filtro aplicado ao recarregar após aprovar/reprovar (antes a lista voltava para “todos”). */
  const filtrosCorregedoriaRef = useRef<CorregedoriaFilterOptions>({
    statusFiltro: 'todos',
    apenasAguardando: false,
  });

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      if (permissions.relatoriosCorregedoria) {
        await carregarRelatorios();
      } else {
        setAccessDenied(true);
      }
      setLoading(false);
    };
    init();
  }, [permissions.relatoriosCorregedoria]);

  const carregarRelatorios = async (filters?: CorregedoriaFilterOptions) => {
    try {
      setLoading(true);
      setAccessDenied(false);
      if (filters) {
        filtrosCorregedoriaRef.current = filters;
      }
      const effective = filtrosCorregedoriaRef.current;
      const { statusFiltro: statusFiltroBruto, ...apiFilters } = effective;
      const statusFiltro = statusFiltroBruto ?? 'todos';
      if (statusFiltro === 'aprovados') {
        apiFilters.apenasAguardando = false;
      } else if (statusFiltro === 'nao_aprovados') {
        apiFilters.apenasAguardando = true;
      } else {
        apiFilters.apenasAguardando = false;
      }
      const data = await BuscarRelatoriosParaCorregedoria(apiFilters);
      const processados =
        statusFiltro === 'aprovados'
          ? data.filter((r) => !!r.corregedoriaAprovadoEm)
          : statusFiltro === 'nao_aprovados'
            ? data.filter((r) => !r.corregedoriaAprovadoEm)
            : data;
      setRelatorios(processados);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setAccessDenied(true);
        setRelatorios([]);
      } else {
        console.error('Erro ao carregar relatórios Corregedoria:', err);
        toast.error('Erro ao carregar relatórios.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (filters: CorregedoriaFilterOptions) => {
    await carregarRelatorios(filters);
  };

  const handleView = (relatorio: RelatorioAtividadeDto) => {
    const params = new URLSearchParams({
      id: String(relatorio.id),
      defensoriaId: String(relatorio.defensoriaId),
      defensoriaName: relatorio.defensoriaName || '',
      mes: String(relatorio.mes).padStart(2, '0'),
      ano: String(relatorio.ano),
      tipo: relatorio.tipo,
      view: 'true',
      origem: 'corregedoria',
    });
    navigate(`${Links.Relatorios_Criar}?${params.toString()}`);
  };

  /** Dias remoto ou ausente sem decisão da Corregedoria (bloqueiam aval final). */
  const comparecimentoPendenteCorregedoria = (r: RelatorioAtividadeDto) => {
    const dias =
      (r.dados?.comparecimento?.diasStatus as Array<{
        status?: string;
        remotoValidado?: boolean;
        ausenteAceitoCorregedoria?: boolean;
      }>) || [];
    return dias.some(
      (d) =>
        (d.status === 'remoto' && d.remotoValidado !== true) ||
        (d.status === 'ausente' && d.ausenteAceitoCorregedoria !== true),
    );
  };

  const handleApprove = (relatorio: RelatorioAtividadeDto) => {
    if (comparecimentoPendenteCorregedoria(relatorio)) {
      setRelatorioParaAprovar(relatorio);
      setShowValidarRemotosModal(true);
      return;
    }
    if (!window.confirm(`Deseja dar aval final ao relatório de ${relatorio.defensorNome || 'o defensor'}?`)) {
      return;
    }
    aprovarComRemotos(relatorio.id, undefined);
  };

  const handleConfirmValidarRemotos = async () => {
    if (!relatorioParaAprovar) return;
    await aprovarComRemotos(relatorioParaAprovar.id, undefined);
    setShowValidarRemotosModal(false);
    setRelatorioParaAprovar(null);
  };

  const aprovarComRemotos = async (relatorioId: number, remotoValidados?: RemotoValidadoItem[]) => {
    try {
      setAproving(true);
      await AprovarCorregedoria(relatorioId, remotoValidados);
      toast.success('Aval final registrado com sucesso!');
      await carregarRelatorios();
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Erro ao aprovar.';
      toast.error(msg);
    } finally {
      setAproving(false);
    }
  };

  const handleApproveBatch = async (selecionados: RelatorioAtividadeDto[]) => {
    if (selecionados.length === 0) return;
    if (!window.confirm(`Deseja dar aval final a ${selecionados.length} relatório(s) selecionado(s)?`)) {
      return;
    }
    try {
      setAproving(true);
      let sucesso = 0;
      let falhas = 0;
      for (const r of selecionados) {
        try {
          await AprovarCorregedoria(r.id);
          sucesso++;
        } catch {
          falhas++;
        }
      }
      if (sucesso > 0) {
        toast.success(`${sucesso} relatório(s) com aval final registrado com sucesso.`);
        await carregarRelatorios();
      }
      if (falhas > 0) {
        toast.error(`${falhas} relatório(s) não puderam ser aprovados.`);
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Erro ao aprovar em lote.';
      toast.error(msg);
    } finally {
      setAproving(false);
    }
  };

  const handleReject = (relatorio: RelatorioAtividadeDto) => {
    setRelatorioParaReprovar(relatorio);
    setShowRejectModal(true);
  };

  const handleConfirmReject = async (justificativa: string, destino?: 'diretor' | 'defensor') => {
    if (!relatorioParaReprovar) return;
    try {
      setRejecting(true);
      await ReprovarCorregedoria(relatorioParaReprovar.id, justificativa, destino);
      toast.success(
        destino === 'diretor'
          ? 'Relatório devolvido ao diretor regional.'
          : 'Relatório reprovado. O defensor poderá alterar e reenviar ao diretor.',
      );
      setShowRejectModal(false);
      setRelatorioParaReprovar(null);
      await carregarRelatorios();
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Erro ao reprovar.';
      toast.error(msg);
    } finally {
      setRejecting(false);
    }
  };

  return (
    <Fragment>
      <Container fluid>
        <div className="mb-4">
          <div className="d-flex align-items-end justify-content-between gap-2">
            <h2 className="mb-0">Corregedoria - Análises de relatórios.</h2>
            <div className="d-flex align-items-center gap-2">
              <RelatoriosUserLabel className="mb-1" />
            </div>
          </div>
          <p className="text-muted mb-0">
            Avaliação final dos relatórios enviados pelos defensores e corrigidos pelos diretores.
          </p>
          <hr />
        </div>

        {accessDenied && (
          <Alert variant="warning">
            <strong>Acesso restrito.</strong> Apenas usuários com lotação na Corregedoria (setor Corregedoria) ou com
            permissão administrativa de suporte (ADMIN_ACTIONS com leitura) podem acessar esta área.
          </Alert>
        )}

        {!accessDenied && (
          <>
            <RelatoriosCorregedoriaFilter onFilterChange={handleFilterChange} />
            <RelatoriosCorregedoriaList
              relatorios={relatorios}
              loading={loading}
              onView={handleView}
              onApprove={handleApprove}
              onReject={handleReject}
              onApproveBatch={handleApproveBatch}
              approvingBatch={aproving}
            />
          </>
        )}
      </Container>

      <ModalReprovacao
        show={showRejectModal}
        relatorio={relatorioParaReprovar}
        onHide={() => {
          setShowRejectModal(false);
          setRelatorioParaReprovar(null);
        }}
        onConfirm={handleConfirmReject}
        loading={rejecting}
        contexto="corregedoria"
      />

      <ModalValidarRemotos
        show={showValidarRemotosModal}
        relatorio={relatorioParaAprovar}
        onHide={() => {
          setShowValidarRemotosModal(false);
          setRelatorioParaAprovar(null);
        }}
        onConfirm={handleConfirmValidarRemotos}
        loading={aproving}
      />
    </Fragment>
  );
}
